import { motion } from 'framer-motion'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Activity } from 'lucide-react'

interface MonthlyUsage {
  year: number
  month: number
  month_label: string
  mcp_tool: number
  playground_message: number
  external_mcp: number
  total: number
}

interface CreditUsageHistoryProps {
  data: { months: MonthlyUsage[] }
}

const COLORS = {
  mcp_tool: '#6366f1',
  playground_message: '#22d3ee',
  external_mcp: '#f59e0b',
}

const LABELS: Record<string, string> = {
  mcp_tool: 'MCP Tools',
  playground_message: 'Agent',
  external_mcp: 'External',
}

interface TooltipPayloadItem {
  value?: number
  dataKey?: string
  fill?: string
}

interface CustomTooltipProps {
  active?: boolean
  payload?: TooltipPayloadItem[]
  label?: string
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null
  const total = payload.reduce((sum, p) => sum + (p.value || 0), 0)
  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-sm shadow-md">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: p.fill }} />
            <span className="text-muted-foreground">{p.dataKey ? LABELS[p.dataKey] : ''}</span>
          </div>
          <span className="font-mono tabular-nums">{p.value}</span>
        </div>
      ))}
      <div className="border-t mt-1.5 pt-1.5 flex justify-between font-medium">
        <span>Total</span>
        <span className="font-mono tabular-nums">{total}</span>
      </div>
    </div>
  )
}

export function CreditUsageHistory({ data }: CreditUsageHistoryProps) {
  if (!data?.months?.length) return null

  const chronological = [...data.months].reverse()
  const totalAll = chronological.reduce((s, m) => s + m.total, 0)
  const avgMonthly = Math.round(totalAll / chronological.length)
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-muted-foreground" />
                Usage History
              </CardTitle>
              <CardDescription>Monthly credit consumption</CardDescription>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold tabular-nums">{totalAll.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">
                {chronological.length > 1 ? `${avgMonthly}/mo avg` : 'this period'}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Accountability table — always shown */}
          <div className="rounded-md border">
            <div className="grid grid-cols-5 text-xs font-medium text-muted-foreground bg-muted/50 px-3 py-2 border-b">
              <span>Month</span>
              <span className="text-right">Tools</span>
              <span className="text-right">Agent</span>
              <span className="text-right">External</span>
              <span className="text-right">Total</span>
            </div>
            <div className="max-h-52 overflow-y-auto divide-y divide-border/50">
              {data.months.map((m) => (
                <div
                  key={`${m.year}-${m.month}`}
                  className="grid grid-cols-5 text-xs px-3 py-2 hover:bg-muted/30 transition-colors"
                >
                  <span className="font-medium">{m.month_label}</span>
                  <span className="text-right tabular-nums">{m.mcp_tool || '—'}</span>
                  <span className="text-right tabular-nums">{m.playground_message || '—'}</span>
                  <span className="text-right tabular-nums">{m.external_mcp || '—'}</span>
                  <span className="text-right tabular-nums font-semibold">{m.total}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bar chart — only when 2+ months */}
          {chronological.length >= 2 && (
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chronological} barCategoryGap="30%" maxBarSize={40}>
                  <XAxis
                    dataKey="month_label"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={32}
                    allowDecimals={false}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
                  />
                  <Bar
                    dataKey="mcp_tool"
                    stackId="a"
                    fill={COLORS.mcp_tool}
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="playground_message"
                    stackId="a"
                    fill={COLORS.playground_message}
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="external_mcp"
                    stackId="a"
                    fill={COLORS.external_mcp}
                    radius={[3, 3, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Legend inline */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <span
                className="w-2.5 h-2.5 rounded-sm"
                style={{ backgroundColor: COLORS.mcp_tool }}
              />
              MCP Tools
            </div>
            <div className="flex items-center gap-1.5">
              <span
                className="w-2.5 h-2.5 rounded-sm"
                style={{ backgroundColor: COLORS.playground_message }}
              />
              Agent
            </div>
            <div className="flex items-center gap-1.5">
              <span
                className="w-2.5 h-2.5 rounded-sm"
                style={{ backgroundColor: COLORS.external_mcp }}
              />
              External
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}
